import { useTheme } from '@material-ui/core';
import React, { useMemo } from 'react';
import { components } from 'react-select';
import LoadingIndicator from '~components/react-select/loading-indicator';
import { ArrowV } from '~static/svg-components';
import hexToRGBA from '~utils/hex-to-rgba';
import { useHeaderSticky } from './use-header-sticky';

export const useContentHeaderSelectStyles = () => {
  const theme = useTheme();
  const headerSticky = useHeaderSticky();

  return useMemo(
    () => ({
      control: (provided) => ({
        ...provided,
        ...(headerSticky
          ? { backgroundColor: theme.colors.brandBg, boxShadow: 'none' }
          : { boxShadow: theme.shadow.componentShallow }),
        border: 'none',
        borderRadius: theme.borderRadius.full,
        paddingLeft: 12,
        paddingRight: 14,
        minHeight: 32,
        width: 160,
        ':hover': {
          border: 'none',
        },
        ':active': {
          border: 'none',
        },
        cursor: 'pointer',
      }),
      dropdownIndicator: () => ({
        marginLeft: 2,
        fontSize: 10,
        color: theme.colors.brandMedium,
      }),
      group: () => ({
        marginTop: 8,
      }),
      groupHeading: () => ({
        ...theme.typography.caption,
        display: 'inline-block',
        color: theme.colors.grey66,
        marginLeft: 8,
        marginRight: 8,
        marginBottom: 0,
        padding: '1px 8px',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        backgroundColor: theme.colors.greyE8,
        borderRadius: theme.borderRadius.full,
      }),
      menu: (provided) => ({
        ...provided,
        boxShadow: `0 2px 8px 0 ${hexToRGBA(theme.colors.dark, 0.16)}`,
        marginBottom: 2,
        marginTop: 2,
      }),
      menuList: (provided) => ({
        ...provided,
        maxHeight: 328,
      }),
      option: (provided, state) => ({
        ...provided,
        ...theme.typography.dropdown,
        ':active': { backgroundColor: 'inherit' },
        padding: '6px 12px',
        color: state.isSelected
          ? theme.colors.brandAction
          : state.isDisabled
          ? theme.colors.grey99
          : theme.colors.dark,
        backgroundColor: state.isFocused ? theme.colors.brandBg : provided.background,
      }),
      placeholder: (provided) => ({
        ...provided,
        ...theme.typography.h6,
        color: theme.colors.grey99,
      }),
      singleValue: (provided) => ({
        ...provided,
        ...theme.typography.h6,
        maxWidth: 'calc(100% - 20px)',
        color: theme.colors.brandIcon,
      }),
    }),
    [
      headerSticky,
      theme.borderRadius.full,
      theme.colors.brandAction,
      theme.colors.brandBg,
      theme.colors.brandIcon,
      theme.colors.brandMedium,
      theme.colors.dark,
      theme.colors.grey99,
      theme.colors.greyE8,
      theme.colors.grey66,
      theme.shadow.componentShallow,
      theme.typography.caption,
      theme.typography.dropdown,
      theme.typography.h6,
    ]
  );
};

const DropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}>
    <ArrowV
      style={{
        display: 'flex',
        fontSize: 'inherit',
        transform: props.selectProps.menuIsOpen ? 'rotate(270deg)' : 'rotate(90deg)',
        transition: 'transform 150ms cubic-bezier(0.65, 0.05, 0.36, 1)',
      }}
    />
  </components.DropdownIndicator>
);

const Control = (props) => {
  const { Icon, children, ...otherProps } = props;

  return (
    <components.Control {...otherProps}>
      {Icon && <Icon color="brandIcon" />}
      {props.children}
    </components.Control>
  );
};

export const contentHeaderSelectComponents = {
  Control,
  DropdownIndicator,
  LoadingIndicator,
  IndicatorSeparator: null,
};
